import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Minus, Plus } from 'lucide-react';
import { useIsFetching } from 'react-query';
import { useShell } from './ShellContext';

export default function StatusBar() {
  const navigate = useNavigate();
  const {
    badgeCounts,
    invoicingProgress,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    canIncreaseFontSize,
    canDecreaseFontSize,
    openMessageInbox,
  } = useShell();
  const [clock, setClock] = useState(() => format(new Date(), 'h:mm a'));
  const isFetching = useIsFetching();

  useEffect(() => {
    const timer = setInterval(() => {
      setClock(format(new Date(), 'h:mm a'));
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  return (
    <footer className="hub-status-bar">
      <button
        type="button"
        className="hub-status-chip"
        onClick={() => navigate('/dispatch')}
        title="Open dispatch backlog"
      >
        <span className="text-gray-500">Unassigned</span>
        <span className="font-semibold text-gray-800">{badgeCounts.unassigned}</span>
      </button>

      {badgeCounts.overdue > 0 && (
        <button
          type="button"
          className="hub-status-chip border-red-200 bg-red-50 hover:bg-red-100"
          onClick={() => navigate('/dispatch')}
        >
          <span className="text-red-600">Overdue</span>
          <span className="font-semibold text-red-700">{badgeCounts.overdue}</span>
        </button>
      )}

      <button
        type="button"
        className="hub-status-chip"
        onClick={() => navigate('/invoicing')}
      >
        <span className="text-gray-500">To finalize</span>
        <span className="font-semibold text-gray-800">{badgeCounts.toFinalize}</span>
      </button>

      {badgeCounts.parts > 0 && (
        <button
          type="button"
          className="hub-status-chip"
          onClick={() => navigate('/parts-needed')}
        >
          <span className="text-gray-500">Parts</span>
          <span className="font-semibold text-gray-800">{badgeCounts.parts}</span>
        </button>
      )}

      {badgeCounts.messages > 0 && (
        <button
          type="button"
          className="hub-status-chip border-primary-200 bg-primary-50 hover:bg-primary-100"
          onClick={() => openMessageInbox()}
        >
          <span className="text-primary-700">Messages</span>
          <span className="font-semibold text-primary-800">{badgeCounts.messages}</span>
        </button>
      )}

      {invoicingProgress && (
        <span className="text-gray-600 ml-2">
          Session: {invoicingProgress.finalized} finalized
          {invoicingProgress.remaining != null && ` · ${invoicingProgress.remaining} remaining`}
        </span>
      )}

      <div className="flex-1" />

      <div className="inline-flex items-center gap-0.5 border border-surface-border rounded-md bg-surface-secondary">
        <button
          type="button"
          onClick={decreaseFontSize}
          disabled={!canDecreaseFontSize}
          className="p-1 rounded-l-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed"
          title="Decrease font size"
          aria-label="Decrease font size"
        >
          <Minus className="h-3 w-3" />
        </button>
        <span className="px-1.5 text-2xs tabular-nums text-gray-600 min-w-[2rem] text-center">{fontSize}px</span>
        <button
          type="button"
          onClick={increaseFontSize}
          disabled={!canIncreaseFontSize}
          className="p-1 rounded-r-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed"
          title="Increase font size"
          aria-label="Increase font size"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      <span className="inline-flex items-center gap-1.5 ml-2">
        {isFetching > 0 ? (
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />
            Syncing
          </>
        ) : (
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Up to date
          </>
        )}
      </span>

      <span className="tabular-nums">{clock}</span>
    </footer>
  );
}
